import { Input, Text } from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";
import { useIsWorkplaceReviewUXUpdateEnabled } from "@src/appV2/PostShiftReview/hooks/useIsWorkplaceReviewUXUpdateEnabled";
import { Button } from "@src/appV2/redesign/components/Button";
import { SHIFT_DISCOVERY_WORKPLACE_REVIEWS_COMMENT_MODAL_PATH } from "@src/appV2/redesign/ShiftDiscovery/paths";
import { useWorkplaceModalsDataContext } from "@src/appV2/redesign/ShiftDiscovery/useWorkplaceModalsDataContext";
import {
  type PaginatedWorkplaceCommentsParams,
  usePaginatedWorkplaceComments,
} from "@src/appV2/redesign/Workplace/Review/api/usePaginatedWorkplaceComments";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { parseISO } from "date-fns";
import { debounce } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { WorkplaceReviewCommentCard } from "./Card";
import { COMMENTS_PAGE_SIZE, LEGACY_COMMENTS_PAGE_SIZE } from "./constants";
import { InlineRepliesSection } from "./InlineRepliesSection";
import { InlineReplyInput } from "./InlineReplyInput";
import { useUpdateCachedComment } from "./useUpdateCachedComment";

interface WorkplaceCommentListProps {
  workplaceId: string;
}

export function WorkplaceCommentList(props: WorkplaceCommentListProps) {
  const { workplaceId } = props;

  const history = useHistory();
  const worker = useDefinedWorker();
  const { isEnabled: isReviewUXUpdateEnabled } = useIsWorkplaceReviewUXUpdateEnabled();

  const { generateModalPath } = useWorkplaceModalsDataContext();
  const commentsPageSize = isReviewUXUpdateEnabled ? COMMENTS_PAGE_SIZE : LEGACY_COMMENTS_PAGE_SIZE;
  const commentsParams: PaginatedWorkplaceCommentsParams = {
    workplaceId,
    page: { size: commentsPageSize },
  };
  const {
    data: commentsData,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = usePaginatedWorkplaceComments(commentsParams);

  const inputRef = useRef<HTMLInputElement>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const allComments = useMemo(() => {
    if (!commentsData) {
      return [];
    }

    return commentsData.pages.flatMap((page) => page.data);
  }, [commentsData]);

  const filteredComments = useMemo(() => {
    const sortedComments = [...allComments].sort((a, b) => {
      return (
        parseISO(b.attributes.createdAt).getTime() - parseISO(a.attributes.createdAt).getTime()
      );
    });

    if (!searchTerm) {
      return sortedComments;
    }

    const lowercaseSearch = searchTerm.toLowerCase();
    return sortedComments.filter((comment) =>
      comment.attributes.text.toLowerCase().includes(lowercaseSearch)
    );
  }, [allComments, searchTerm]);

  const debouncedSetSearchTerm = useMemo(
    () =>
      debounce((term: string) => {
        setSearchTerm(term);
      }, 300),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSetSearchTerm.cancel();
    };
  }, [debouncedSetSearchTerm]);

  const { updateComment, deleteComment } = useUpdateCachedComment({
    workplaceId: String(workplaceId),
    commentsParams,
  });

  return (
    <Stack spacing={7}>
      <Text semibold variant="h6" sx={{ paddingLeft: 5 }}>
        Reviews
      </Text>

      <Input
        ref={inputRef}
        iconType="search"
        placeholder="Search reviews..."
        size="small"
        onChange={(event) => {
          debouncedSetSearchTerm(event.target.value);
        }}
      />

      <Stack spacing={3}>
        {isLoading && (
          <Text variant="h6" sx={{ paddingTop: 7, textAlign: "center" }}>
            Loading reviews...
          </Text>
        )}

        {!isLoading && allComments.length === 0 && (
          <Text variant="h6" sx={{ paddingTop: 7, textAlign: "center" }}>
            No reviews yet.
          </Text>
        )}

        {!isLoading && allComments.length > 0 && filteredComments.length === 0 && (
          <Text variant="h6" sx={{ paddingTop: 7, textAlign: "center" }}>
            No reviews matching your search
          </Text>
        )}

        {filteredComments.map((comment) => (
          <WorkplaceReviewCommentCard
            key={comment.id}
            comment={comment}
            workplaceId={String(workplaceId)}
            viewerIsAuthor={comment.attributes.workerInfo.id === worker.userId}
            onClick={() => {
              history.push(
                generateModalPath(SHIFT_DISCOVERY_WORKPLACE_REVIEWS_COMMENT_MODAL_PATH, {
                  workplaceId,
                  commentId: comment.id,
                })
              );
            }}
            onLikeChanged={(newComment) => {
              updateComment(newComment);
            }}
            onAnonymized={(newComment) => {
              updateComment(newComment);
            }}
            onRemoved={(commentId) => {
              deleteComment(commentId);
            }}
          >
            {isReviewUXUpdateEnabled && (
              <>
                <InlineRepliesSection
                  parentComment={comment}
                  workplaceId={workplaceId}
                  viewerUserId={worker.userId}
                />
                <InlineReplyInput
                  workplaceId={workplaceId}
                  parentId={comment.id}
                  workerFullName={worker.name}
                />
              </>
            )}
          </WorkplaceReviewCommentCard>
        ))}

        {hasNextPage && (
          <Button
            variant="outlined"
            disabled={isFetchingNextPage}
            sx={{ alignSelf: "center" }}
            onClick={async () => {
              await fetchNextPage();
            }}
          >
            {isFetchingNextPage ? "Loading more..." : "Load more reviews"}
          </Button>
        )}
      </Stack>
    </Stack>
  );
}
